import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { api, mesajEroare } from "./api.js";
import { UM, um } from "./um.js";
import { tr } from "./i18n.js";
// Încărcat abia la deschidere: biblioteca de citit Excel are câteva sute de
// kilobytes, iar majoritatea oamenilor care deschid catalogul nu importă
// nimic. N-au de ce să-i plătească la fiecare încărcare de pagină.
const ImportCatalog = lazy(() => import("./ImportCatalog.jsx"));

const ADMINISTRARE = ["ADMIN_TENANT", "MANAGER_TEHNIC", "MAGAZIONER"];
const POATE_PRET = ["ADMIN_TENANT", "MANAGER_TEHNIC", "FINANCIAR"];

/**
 * Cine vede bani. Oglindește lista din backend (common/constants/roles.ts) —
 * dar interfața doar ascunde coloanele; adevărata filtrare e acolo, în
 * selecția de câmpuri. Dacă cele două ar diverge, tehnicianul ar vedea niște
 * coloane goale, nu date pe care n-are voie să le vadă.
 */
const VEDE_BANI = ["ADMIN_TENANT", "MANAGER_TEHNIC", "FINANCIAR", "MAGAZIONER", "AUDITOR"];

const TIPURI = [
  { cod: "Consumabil", titlu: "Consumabile" },
  { cod: "Manopera", titlu: "Manoperă" },
  { cod: "Serviciu", titlu: "Servicii" },
  { cod: "Deplasare", titlu: "Deplasare" },
  { cod: "Echipament", titlu: "Echipamente" },
];

/** Unitățile vin din bază ca „Bucata"; pe ecran se citesc ca „buc". */
function bani(v) {
  const n = Number(v);
  return n.toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Catalog({ eu }) {
  const [tip, setTip] = useState("Consumabil");
  const [articole, setArticole] = useState([]);
  const [cate, setCate] = useState({ total: 0, plafon: 0 });
  const [stare, setStare] = useState(null);
  const [incarca, setIncarca] = useState(true);
  const [eroare, setEroare] = useState("");
  const [filtru, setFiltru] = useState("");
  const [filtruCerut, setFiltruCerut] = useState("");
  const [editat, setEditat] = useState(null);
  const [formular, setFormular] = useState(false);
  const [importa, setImporta] = useState(false);
  const [aratăDezactivate, setAratăDezactivate] = useState(false);

  const poateAdministra = ADMINISTRARE.includes(eu.rol);
  const poateSchimbaPret = POATE_PRET.includes(eu.rol);
  const vedeBani = VEDE_BANI.includes(eu.rol);

  /*
   * Filtrul se trimite la SERVER, nu se aplică în browser. Catalogul unei
   * firme reale are zeci de mii de poziții și vine plafonat: o filtrare locală
   * ar căuta doar în ce a apucat să vină, iar articolul căutat ar lipsi fără
   * ca ecranul să spună de ce.
   */
  const reincarca = useCallback(async () => {
    setIncarca(true);
    try {
      const r = await api.articole(tip, undefined, aratăDezactivate, filtruCerut);
      setArticole(r.articole ?? []);
      setCate({ total: r.total ?? 0, plafon: r.plafon ?? 0 });
      // Doar cine poate ATINGE prețurile cere starea lor. Cerută și de cine
      // administrează articolele, ajungea un refuz în față pentru magazioner:
      // el ține articolele, dar prețurile sunt decizie comercială.
      if (poateSchimbaPret) setStare(await api.starePreturi());
    } catch (e) {
      setEroare(mesajEroare(e));
    } finally {
      setIncarca(false);
    }
  }, [tip, poateAdministra, poateSchimbaPret, aratăDezactivate, filtruCerut]);

  useEffect(() => { reincarca(); }, [reincarca]);

  // O pauză scurtă între taste: altfel fiecare literă ar fi o cerere.
  useEffect(() => {
    const t = setTimeout(() => setFiltruCerut(filtru.trim()), 300);
    return () => clearTimeout(t);
  }, [filtru]);

  const peFamilii = useMemo(() => {
    const grupe = new Map();
    for (const a of articole) {
      const f = a.familie || "Neîncadrate";
      if (!grupe.has(f)) grupe.set(f, []);
      grupe.get(f).push(a);
    }
    return [...grupe.entries()];
  }, [articole]);

  async function actiune(promisiune) {
    setEroare("");
    try { await promisiune; await reincarca(); }
    catch (e) { setEroare(mesajEroare(e)); }
  }

  return (
    <div className="continut">
      <h2>{tr("Catalog de articole")}</h2>
      <p className="subtitlu">
        {tr("Ce se consumă și ce se facturează. De aici își alege tehnicianul materialele folosite într-o intervenție, iar devizul își ia prețurile.")}
      </p>

      {eroare && <div className="eroare">{eroare}</div>}

      {stare && stare.faraPret > 0 && (
        <div className="panou panou-atentie">
          <div className="panou-corp">
            <strong>{stare.faraPret} {tr("din")} {stare.total} {tr("articole n-au încă preț.")}</strong>{" "}
            {tr("Le-am livrat cu prețul zero în mod deliberat — un preț inventat într-o aplicație care calculează devize e mai periculos decât unul lipsă, fiindcă trece neobservat în factură. Completează-le pe cele pe care le folosești.")}
          </div>
        </div>
      )}

      <div className="taburi-sisteme">
        {TIPURI.map((t) => (
          <button
            key={t.cod}
            className={"tab-sistem" + (t.cod === tip ? " activ" : "")}
            onClick={() => { setTip(t.cod); setFiltru(""); }}
          >
            {tr(t.titlu)}
          </button>
        ))}
      </div>

      <input
        className="camp camp-cautare"
        placeholder={tr("Caută după cod, denumire sau cod de producător…")}
        value={filtru}
        onChange={(e) => setFiltru(e.target.value)}
      />

      {/* O listă tăiată care nu spune că e tăiată e mai rea decât una lungă. */}
      {cate.total > articole.length && (
        <p className="nota-cautare">
          {tr("Se afișează primele {0} din {1} articole. Caută mai sus ca să ajungi la restul.", articole.length, cate.total)}
        </p>
      )}

      {poateAdministra && (
        <label className="comutator-dezactivate">
          <input
            type="checkbox"
            checked={aratăDezactivate}
            onChange={(e) => setAratăDezactivate(e.target.checked)}
          />
          {tr("arată și articolele dezactivate")}
        </label>
      )}

      {incarca ? (
        <div className="gol" style={{ padding: "2rem 0" }}>{tr("Se încarcă…")}</div>
      ) : peFamilii.length === 0 ? (
        <div className="panou">
          <div className="panou-corp gol">
            {tip === "Echipament"
              ? tr("Niciun articol de echipament. Aici intră produsele concrete ale furnizorilor tăi — „Detector PIR Bosch ISC-BPR2-W12”, cu cod și preț. Nu le-am inventat: vin din lista de prețuri a furnizorului, adăugate manual sau importate.")
              : tr("Niciun articol în această categorie.")}
          </div>
        </div>
      ) : (
        peFamilii.map(([familie, lista]) => (
          <div className="panou" key={familie}>
            <div className="panou-antet">
              <span>{tr(familie)}</span>
              <span className="numar">{lista.length} {tr("articole")}</span>
            </div>
            <div className="panou-corp">
              <table className="tabel tabel-catalog">
                <thead>
                  <tr>
                    <th>{tr("Denumire")}</th>
                    <th>{tr("Cod")}</th>
                    <th>UM</th>
                    {vedeBani && <th className="num">{tr("Achiziție")}</th>}
                    {vedeBani && <th className="num">{tr("Vânzare")}</th>}
                    {vedeBani && <th className="num">{tr("Marjă")}</th>}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((a) => {
                    const fara = Number(a.pretVanzare) === 0;
                    const marja = Number(a.pretVanzare) - Number(a.costAchizitie ?? 0);
                    return (
                      <tr key={a.id} className={
                        (a.activ === false ? "tip-dezactivat " : "") + (fara && vedeBani && a.activ !== false ? "rand-fara-pret" : "")
                      }>
                        <td>
                          {tr(a.denumire)}
                          {a.activ === false && <span className="eticheta eticheta-inactiv">{tr("dezactivat")}</span>}
                          {a.factorAmbalare > 1 && (
                            <span className="marcaj" title={tr("Se achiziționează la pachet, se consumă la bucată")}>
                              {tr("pachet de")} {a.factorAmbalare}
                            </span>
                          )}
                        </td>
                        <td><code>{a.cod}</code></td>
                        <td>{tr(UM[a.unitateMasura] || a.unitateMasura)}</td>
                        {vedeBani && <td className="num">{bani(a.costAchizitie ?? 0)}</td>}
                        {vedeBani && (
                          <td className="num">
                            {fara ? <span className="fara-pret">{tr("necompletat")}</span> : bani(a.pretVanzare)}
                          </td>
                        )}
                        {vedeBani && (
                          <td className={"num " + (marja < 0 ? "marja-negativa" : "")}>
                            {fara ? "—" : bani(marja)}
                          </td>
                        )}
                        <td className="celula-actiuni">
                          {a.activ === false ? (
                            poateAdministra && (
                              <button className="buton buton-mic" onClick={() => actiune(api.reactiveazaArticol(a.id))}>
                                {tr("reactivează")}
                              </button>
                            )
                          ) : (
                            <>
                              {poateSchimbaPret && (
                                <button className="buton buton-mic" onClick={() => setEditat(a)}>
                                  {fara ? tr("pune preț") : tr("schimbă prețul")}
                                </button>
                              )}
                              {poateAdministra && (
                                <button
                                  className="buton buton-mic buton-sters"
                                  onClick={() => actiune(api.dezactiveazaArticol(a.id))}
                                  title={tr("Dispare din listele de selecție noi, dar rămâne pe fișele care îl conțin. Reversibil.")}
                                >
                                  {tr("dezactivează")}
                                </button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {poateAdministra && (
        <div className="actiuni-catalog">
          <button className="buton buton-adauga" onClick={() => setFormular(true)}>
            {tr("+ articol nou")}
          </button>
          {poateSchimbaPret && (
            <button className="buton buton-sters" onClick={() => setImporta(true)}>
              {tr("importă din Excel")}
            </button>
          )}
        </div>
      )}

      {editat && (
        <FormularPret
          articol={editat}
          laInchidere={() => setEditat(null)}
          laSalvare={async (date) => { await actiune(api.schimbaPret(editat.id, date)); setEditat(null); }}
        />
      )}

      {importa && (
        <Suspense fallback={<div className="modal-fundal"><div className="modal">{tr("Se încarcă…")}</div></div>}>
          <ImportCatalog laInchidere={() => setImporta(false)} laTerminare={reincarca} />
        </Suspense>
      )}

      {formular && (
        <FormularArticol
          tip={tip}
          laInchidere={() => setFormular(false)}
          laSalvare={async (date) => { await actiune(api.creeazaArticol(date)); setFormular(false); }}
        />
      )}
    </div>
  );
}

function FormularPret({ articol, laInchidere, laSalvare }) {
  const [cost, setCost] = useState(String(articol.costAchizitie ?? 0));
  const [pret, setPret] = useState(String(articol.pretVanzare ?? 0));
  const [motiv, setMotiv] = useState("");
  const marja = Number(pret) - Number(cost);
  const valid = Number(cost) >= 0 && Number(pret) >= 0;

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{tr(articol.denumire)}</h3>
        <p className="subtitlu">
          {tr("Prețul vechi nu se pierde: se închide și rămâne în istoric, ca o fișă emisă înainte de schimbare să dea aceleași cifre și peste ani.")}
        </p>
        <div className="formular-linie">
          <label>
            {tr("Cost de achiziție")}
            <input className="camp" type="number" step="0.0001" min="0" value={cost} onChange={(e) => setCost(e.target.value)} autoFocus />
          </label>
          <label>
            {tr("Preț de vânzare")}
            <input className="camp" type="number" step="0.0001" min="0" value={pret} onChange={(e) => setPret(e.target.value)} />
          </label>
        </div>
        <p className={"nota-cautare " + (marja < 0 ? "marja-negativa" : "")}>
          {tr("Marjă:")} {bani(marja)} {marja < 0 && "— vinzi sub cost."}
        </p>
        <div className="formular-linie">
          <label>
            {tr("Motivul schimbării (opțional)")}
            <input className="camp" value={motiv} onChange={(e) => setMotiv(e.target.value)} placeholder={tr("Listă de prețuri furnizor, august 2026")} />
          </label>
        </div>
        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("renunță")}</button>
          <button className="buton" disabled={!valid}
            onClick={() => laSalvare({ costAchizitie: Number(cost), pretVanzare: Number(pret), motiv: motiv.trim() || undefined })}>
            {tr("salvează")}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormularArticol({ tip, laInchidere, laSalvare }) {
  const [garantie, setGarantie] = useState("");
  const [producator, setProducator] = useState("");
  const [cod, setCod] = useState("");
  const [denumire, setDenumire] = useState("");
  const [familie, setFamilie] = useState("");
  const [um, setUm] = useState("Bucata");
  const [ambalare, setAmbalare] = useState("1");
  const lipsesc = [
    cod.trim().length < 2 && tr("un cod de cel puțin 2 caractere"),
    denumire.trim().length < 2 && tr("o denumire de cel puțin 2 caractere"),
  ].filter(Boolean);
  const gata = lipsesc.length === 0;

  return (
    <div className="modal-fundal" onClick={laInchidere}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>{tr("Articol nou")}</h3>
        <p className="subtitlu">{tr("Se adaugă în categoria „")}{tip}{tr("”. Prețurile le pui separat, după creare.")}</p>
        <div className="formular-linie">
          <label>
            {tr("Denumire")}
            <input className="camp" value={denumire} onChange={(e) => setDenumire(e.target.value)} autoFocus />
          </label>
          <label>
            {tr("Cod")}
            <input className="camp" value={cod} onChange={(e) => setCod(e.target.value.toUpperCase())} />
          </label>
        </div>
        <div className="formular-linie">
          <label>
            {tr("Familie")}
            <input className="camp" value={familie} onChange={(e) => setFamilie(e.target.value)} placeholder={tr("Fixare și montaj")} />
          </label>
          <label>
            {tr("Unitate de măsură")}
            <select className="camp" value={um} onChange={(e) => setUm(e.target.value)}>
              {Object.entries(UM).map(([k, v]) => <option key={k} value={k}>{tr(v)}</option>)}
            </select>
          </label>
        </div>
        <div className="formular-linie">
          <label>
            {tr("Factor de ambalare")}
            <input className="camp" type="number" min="1" value={ambalare} onChange={(e) => setAmbalare(e.target.value)} />
          </label>
          <label>
            {tr("Producător")}
            <input className="camp" value={producator} placeholder={tr("Bosch, Hikvision…")}
              onChange={(e) => setProducator(e.target.value)} />
          </label>
        </div>
        <p className="nota-cautare">
          {tr("Factorul de ambalare e pentru lucrurile cumpărate la pachet și consumate la bucată — o cutie de 100 de șuruburi.")}
        </p>
        {tip === "Echipament" && (
          <>
            <div className="formular-linie">
              <label>
                {tr("Garanție standard (luni)")}
                <input className="camp" type="number" min="0" max="120" value={garantie}
                  placeholder="24" onChange={(e) => setGarantie(e.target.value)} />
              </label>
            </div>
            <p className="nota-cautare">
              {tr("Garanția pe care o dă furnizorul. Un echipament montat în parc din acest articol o moștenește, fără să fie scrisă de mână la fiecare bucată.")}
            </p>
          </>
        )}
        {lipsesc.length > 0 && (
          <p className="subtitlu">{tr("Completează:")} {lipsesc.join(", ")}.</p>
        )}

        <div className="modal-actiuni">
          <button className="buton buton-sters" onClick={laInchidere}>{tr("renunță")}</button>
          <button className="buton" disabled={!gata}
            onClick={() => laSalvare({
              cod: cod.trim(), denumire: denumire.trim(), tip,
              familie: familie.trim() || undefined,
              unitateMasura: um, factorAmbalare: Number(ambalare) || 1,
              ...(producator.trim() ? { producator: producator.trim() } : {}),
              ...(tip === "Echipament" && garantie !== ""
                ? { garantieStandardLuni: Number(garantie) } : {}),
            })}>
            {tr("adaugă")}
          </button>
        </div>
      </div>
    </div>
  );
}
